import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useEffect, useMemo, useState } from 'react'
import { StepperComponentV2 } from '@labrav/react-components'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { LoadingTypes, isLoading, useAccountId } from '@labrav/common'

import { useStyles } from './RenewOfferSubscription.styles'
import { useDispatch, useSelector } from '../../../store'
import type { RootState } from '../../../store/types'
import { SubscriptionSectionWrapper } from '../SubscriptionSectionWrapper/SubscriptionSectionWrapper'
import { SubscriptionSelectionModal } from '../SubscriptionSelectionModal/SubscriptionSelectionModal'
import {
  clearAgreementBasedOffer,
  clearCurrentSubscriptionDetail,
  getCurrentSubscriptionDetail,
  getInProgressAgreementBasedOffer,
} from '../../../modules/subscription/action'
import type { ModalSubscriptionsState } from '../../../modules/modalSubscription/reducer'
import { getActiveReceivedResaleAuthorizations } from '../../../modules/subscription/subscriptionsListDeps'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import type { Product } from '../../../modules/productsListing/types'
import { SelectedSubscriptionDetail } from './SelectedSubscriptionDetail/SelectedSubscriptionDetail'
import { RenewSubscriptionForm } from './RenewSubscriptionForm/RenewSubscriptionForm'
import { RenewOfferSubscriptionFooter } from './RenewOfferSubscriptionFooter/RenewOfferSubscriptionFooter'
import {
  stepsForRenewOffer,
  type RenewOfferSubscriptionProps,
} from './renewOfferTypes'
import { ChannelOfferCreationModal } from '../../ChannelOffers/ChannelOfferCreationModal'
import agreementBasedOfferAddEditValidationSchema from '../../../common/utils/validationAgreementBasedOffer'

export { stepsForRenewOffer } from './renewOfferTypes'

export const RenewOfferSubscription: React.FC<RenewOfferSubscriptionProps> = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSalesforce,
  partnerIdFromContext,
  steps = stepsForRenewOffer,
}) => {
  useSyncFlyoutRouteParams(!!externalBaseURL)
  const noOps = () => void 0
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    agreementOfferId,
    subscriptionId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud: string
    accountId: string
    agreementOfferId?: string
    subscriptionId?: string
    secureKey: string
    partnerId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = partnerIdFromContext || routeAccountId || fallbackAccountId
  const location = useLocation()
  const { subscriptionId: subscriptionIdFromState } = location?.state || {}

  const [open, setOpen] = useState<boolean>(false)
  const [cppoSubscription, setCppoSubscription] = useState<string>('')
  const [selectedSubscription, setSelectedSubscription] = useState<string>(
    subscriptionId || subscriptionIdFromState || ''
  )

  const { subscriptions } = useSelector<ModalSubscriptionsState>(
    state => state?.modalSubscription || { subscriptions: [] }
  )

  const loadingActiveResellerAuth = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION)
  )

  const subscriptionDetail = useSelector(
    state =>
      state?.subscriptions?.currentSubscription?.[selectedSubscription] || null
  )
  const agreementBasedOffer = useSelector(
    state =>
      state?.subscriptions?.currentAgreementInCreation?.[
        selectedSubscription
      ] || null
  )
  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    (state: RootState) =>
      (
        state?.receivedResellerAuthorization as {
          selectedActiveReceivedResellerAuthorization?: string
        }
      )?.selectedActiveReceivedResellerAuthorization || ''
  )

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const productId = subscriptionDetail?.productDetails?.labraProductId

  const baseURL = useMemo(
    () => `${externalBaseURL?.replace('/product-listing', '') || '/flyout'}`,
    [externalBaseURL]
  )

  const handleModalClose = () => {
    setOpen(false)
  }
  const handleModalOpen = () => {
    setOpen(true)
  }

  const handleNext = (nextSubscriptionId?: string) => {
    navigate(
      `/flyout/${routeCloud}/${accountId}/subscriptions/${nextSubscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`
    )
  }

  const acceptSelectedSubscription = async (nextSubscriptionId: string) => {
    const selectedSubscriptionItem = subscriptions.find(
      subscriptionItem => subscriptionItem.subscriptionId === nextSubscriptionId
    )
    if (selectedSubscriptionItem?.offerType === 'CPPO') {
      await dispatch(
        getActiveReceivedResaleAuthorizations({
          partnerId: accountId,
          subscriptionId: nextSubscriptionId,
        })
      )
      await setSelectedSubscription(nextSubscriptionId)
      await setCppoSubscription('create')
    } else {
      setOpen(false)
      setSelectedSubscription(nextSubscriptionId)
      const pathName = externalBaseURL
        ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/subscriptions/${nextSubscriptionId}/edit`
        : `/flyout/${routeCloud}/${accountId}/subscriptions/${nextSubscriptionId}/edit`
      navigate(pathName, {
        state: {
          subscriptionId: nextSubscriptionId,
        },
      })
    }
  }

  useEffect(() => {
    if (!isEmpty(accountId) && selectedSubscription) {
      dispatch(
        getCurrentSubscriptionDetail(accountId, selectedSubscription, 'AWS')
      )
    }
  }, [accountId, selectedSubscription, dispatch])

  useEffect(() => {
    if (
      !isEmpty(accountId) &&
      selectedSubscription &&
      !isEmpty(subscriptionDetail) &&
      !isEmpty(products)
    ) {
      if (subscriptionDetail?.subscription?.offerType !== 'CPPO') {
        dispatch(
          getInProgressAgreementBasedOffer(
            accountId,
            selectedSubscription,
            'AWS',
            agreementOfferId
          )
        )
      }
    }
  }, [
    accountId,
    selectedSubscription,
    subscriptionDetail,
    agreementOfferId,
    products,
    dispatch,
  ])

  const footerWidth = externalBaseURL ? '100%' : 'calc(100% - 72px)'

  useEffect(() => {
    if (agreementBasedOffer) {
      if (
        agreementBasedOffer.hasBeenSent &&
        !isEmpty(agreementBasedOffer.privateOfferId)
      ) {
        const productListing =
          externalBaseURL && !isSalesforce
            ? 'product-listing'
            : 'products-listing'

        const offerDetailsPath =
          routeCloud && accountId
            ? externalBaseURL
              ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/${productListing}/${productId}/private-offers/${agreementBasedOffer.privateOfferId}/details`
              : `/flyout/${routeCloud}/${accountId}/${productListing}/${productId}/private-offers/${agreementBasedOffer.privateOfferId}/details`
            : `${baseURL}/${productListing}/${productId}/private-offers/${agreementBasedOffer.privateOfferId}/details`

        navigate(
          {
            pathname: offerDetailsPath,
            search,
          },
          { replace: true }
        )
        return () => {
          dispatch(clearCurrentSubscriptionDetail())
          dispatch(clearAgreementBasedOffer(selectedSubscription))
        }
      } else if (
        !isEmpty(agreementBasedOffer.privateOfferId) &&
        agreementBasedOffer.noOfTimesSaved === 1
      ) {
        const subscriptionEditPath =
          routeCloud && accountId
            ? externalBaseURL
              ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/subscriptions/${selectedSubscription}/edit/${agreementBasedOffer?.privateOfferId}`
              : `/flyout/${routeCloud}/${accountId}/subscriptions/${selectedSubscription}/edit/${agreementBasedOffer?.privateOfferId}`
            : `${baseURL}/subscriptions/${selectedSubscription}/edit/${agreementBasedOffer?.privateOfferId}`

        navigate(
          {
            pathname: subscriptionEditPath,
            search,
          },
          { replace: true }
        )
      }
    }
  }, [
    agreementBasedOffer?.privateOfferId,
    agreementBasedOffer?.hasBeenSent,
    agreementBasedOffer?.noOfTimesSaved,
    selectedSubscription,
    productId,
    externalBaseURL,
    routeCloud,
    accountId,
    secureKeyFromURL,
    partnerIdFromURL,
    baseURL,
    search,
    isSalesforce,
    navigate,
    dispatch,
    agreementBasedOffer,
  ])

  const product =
    products.find(productItem => productItem.productId === productId) ||
    ({} as Product)

  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product])
  const dimensionsOptions = useMemo(() => {
    return productDimensions.map(dimension => dimension.label)
  }, [productDimensions])

  const validationSchema = agreementBasedOfferAddEditValidationSchema(
    timeZone,
    dimensionsOptions
  )

  return (
    <>
      <div className={classes.root} data-testid="subscription-renew-page">
        <StepperComponentV2 activeStep={0} steps={steps} />
        <SubscriptionSectionWrapper
          children={
            <SelectedSubscriptionDetail
              selectedSubscription={selectedSubscription}
              handleModalOpen={handleModalOpen}
            />
          }
          heading="Select contract"
          icon={faListCheck}
        />
        {!isEmpty(agreementBasedOffer) && (
          <Formik
            initialValues={{ ...agreementBasedOffer }}
            validationSchema={validationSchema}
            onSubmit={noOps}
            enableReinitialize
            validateOnMount={true}
          >
            {formikProps => (
              <Form>
                <RenewSubscriptionForm
                  formikProps={formikProps}
                  productId={productId}
                  subscriptionId={selectedSubscription}
                />
                <RenewOfferSubscriptionFooter
                  footerWidth={footerWidth}
                  formikProps={formikProps}
                  crmOpportunityId={crmOpportunityId}
                  crmObjectLink={crmObjectLink}
                  search={search}
                  externalBaseURL={externalBaseURL}
                  partnerIdFromContext={partnerIdFromContext}
                  selectedSubscription={selectedSubscription}
                  productId={subscriptionDetail?.productDetails?.labraProductId}
                />
              </Form>
            )}
          </Formik>
        )}
      </div>
      {isEmpty(agreementBasedOffer) && (
        <RenewOfferSubscriptionFooter
          footerWidth={footerWidth}
          partnerIdFromContext={partnerIdFromContext}
        />
      )}
      {open && (
        <SubscriptionSelectionModal
          open={open}
          handleModalClose={handleModalClose}
          acceptSelectedSubscription={acceptSelectedSubscription}
          currentSelectedSubscriptionId={selectedSubscription}
          acceptLoading={loadingActiveResellerAuth}
          type={'MODAL'}
          isSalesforce={isSalesforce}
        />
      )}
      <ChannelOfferCreationModal
        open={cppoSubscription}
        setOpen={setCppoSubscription}
        handleNext={handleNext}
        subscriptionId={selectedSubscription}
      />
    </>
  )
}
